import { CollapsibleFilters } from '../components/common/CollapsibleFilters';
import React, { useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Alert, Button } from '@mui/joy';
import Box from '@mui/joy/Box';
import Stack from '@mui/joy/Stack';
import Typography from '@mui/joy/Typography';
import Select from '@mui/joy/Select';
import Option from '@mui/joy/Option';
import Input from '@mui/joy/Input';
import Divider from '@mui/joy/Divider';
import Chip from '@mui/joy/Chip';
import CircularProgress from '@mui/joy/CircularProgress';
import { MagnifyingGlass as SearchRounded, Ticket as ConfirmationNumberOutlined } from '@phosphor-icons/react';
import type { SupportTicket } from '../types/support';
import { useTickets } from '../hooks/useSupport';
import { StatusChip, PriorityChip } from '../components/support/TicketStatusChip';
import TicketDetail from '../components/support/TicketDetail';
import { formatDistanceToNow } from 'date-fns';

export default function SupportDashboardPage() {
  const [params, setParams] = useSearchParams();
  const organization = params.get('organization');
  const [page, setPage] = useState(1);
  const [selectedTicket, setSelectedTicket] = useState<SupportTicket | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [priorityFilter, setPriorityFilter] = useState<string>('');
  const [search, setSearch] = useState(params.get('search') || '');

  const filters: Record<string, string> = {page:String(page)};
  if (organization) filters.organization = organization;
  if (params.get('assigned_to')) filters.assigned_to = params.get('assigned_to')!;
  if (statusFilter) filters.status = statusFilter;
  if (priorityFilter) filters.priority = priorityFilter;
  if (search) filters.search = search;

  const { data, isLoading, error, refetch, isFetching } = useTickets(Object.keys(filters).length ? filters : undefined);
  const tickets = data?.results ?? [];
  const totalCount = data?.count ?? 0;

  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
      {/* Header */}
      <Box sx={{ px: 3, pt: 3, pb: 2, flexShrink: 0 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Stack>
            <Stack direction="row" spacing={1.5} alignItems="center">
              <Typography level="h3" sx={{ fontWeight: 700 }}>Support Dashboard</Typography>
              <Chip size="sm" variant="soft" color="primary">{totalCount} tickets</Chip>
            </Stack>
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {organization ? 'Tickets for the selected organization' : 'Manage support tickets across all organizations'}
            </Typography>
          </Stack>
        </Stack>
      </Box>

      {organization && <Box sx={{px:3,pb:1,display:'flex',gap:1,flexWrap:'wrap'}}><Button component="a" href={`/manage/accounts?organization=${organization}`} variant="outlined" size="sm">Back to organization</Button><Button variant="plain" size="sm" onClick={() => {setParams({});setPage(1);setSelectedTicket(null);}}>Show all organizations</Button></Box>}
      <Divider />

      {params.get('assigned_to') && <Box sx={{ px: 3, py: 1 }}><Button size="sm" variant="soft" onClick={() => { const next = new URLSearchParams(params); next.delete('assigned_to'); setParams(next); setPage(1); }}>Assigned staff filter · Clear</Button></Box>}
      {/* Two-panel layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left panel — ticket list (always visible on desktop) */}
        <Box
          sx={{
            width: { xs: selectedTicket ? 0 : '100%', md: 400 },
            minWidth: { md: 400 },
            flexShrink: 0,
            display: { xs: selectedTicket ? 'none' : 'flex', md: 'flex' },
            flexDirection: 'column',
            borderRight: '1px solid',
            borderColor: 'divider',
            overflow: 'hidden',
          }}
        >
          {/* Filters */}
          <CollapsibleFilters><Stack direction="row" spacing={1} sx={{ p: 2, flexShrink: 0 }} flexWrap="wrap" useFlexGap>
            <Input
              size="sm"
              placeholder="Search tickets…"
              startDecorator={<SearchRounded />}
              value={search}
              onChange={e => {setSearch(e.target.value);setPage(1);}}
              sx={{ flex: 1, minWidth: 140 }}
            />
            <Select
              size="sm"
              placeholder="Status"
              value={statusFilter || null}
              onChange={(_, v) => {setStatusFilter(v ?? '');setPage(1);}}
              sx={{ minWidth: 110 }}
            >
              <Option value="">All</Option>
              <Option value="open">Open</Option>
              <Option value="in_progress">In Progress</Option>
              <Option value="waiting_on_customer">Waiting</Option>
              <Option value="resolved">Resolved</Option>
              <Option value="closed">Closed</Option>
            </Select>
            <Select
              size="sm"
              placeholder="Priority"
              value={priorityFilter || null}
              onChange={(_, v) => {setPriorityFilter(v ?? '');setPage(1);}}
              sx={{ minWidth: 100 }}
            >
              <Option value="">All</Option>
              <Option value="urgent">Urgent</Option>
              <Option value="high">High</Option>
              <Option value="normal">Normal</Option>
              <Option value="low">Low</Option>
            </Select>
          </Stack></CollapsibleFilters>

          <Divider />

          {/* Ticket list */}
          <Box sx={{ flex: 1, overflowY: 'auto' }}>
            {isLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
                <CircularProgress size="sm" />
              </Box>
            ) : error ? <Alert color="danger" endDecorator={<Button onClick={() => refetch()}>Retry</Button>}>Could not load support tickets.</Alert> : tickets.length === 0 ? (
              <Box sx={{ textAlign: 'center', py: 8 }}>
                <ConfirmationNumberOutlined size={48} color="var(--joy-palette-text-tertiary)" style={{ marginBottom: 8 }} />
                <Typography level="body-md" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                  No tickets found
                </Typography>
                <Typography level="body-sm" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                  Tickets submitted by users will appear here
                </Typography>
              </Box>
            ) : (
              tickets.map((ticket: SupportTicket) => (
                <Box
                  key={ticket.id}
                  role="button" tabIndex={0} aria-label={`Open ${ticket.subject}`}
                  onKeyDown={event => {if(event.key === 'Enter' || event.key === ' ') {event.preventDefault();setSelectedTicket(ticket);}}}
                  onClick={() => setSelectedTicket(ticket)}
                  sx={{
                    px: 2,
                    py: 1.5,
                    cursor: 'pointer',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                    bgcolor: selectedTicket?.id === ticket.id ? 'primary.softBg' : 'transparent',
                    '&:hover': { bgcolor: selectedTicket?.id === ticket.id ? 'primary.softBg' : 'background.level1' },
                    transition: 'background-color 0.15s',
                  }}
                >
                  <Stack spacing={0.5}>
                    <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={1}>
                      <Typography level="body-sm" sx={{ fontWeight: 600, flex: 1 }} noWrap>
                        {ticket.subject}
                      </Typography>
                      <StatusChip status={ticket.status} />
                    </Stack>
                    <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {ticket.ticket_number}
                      </Typography>
                      <PriorityChip priority={ticket.priority} />
                      <Typography level="body-xs" sx={{ color: 'var(--brand-text, #7013f5)', fontWeight: 500 }}>
                        {ticket.organization_name}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {ticket.created_by
                          ? `${ticket.created_by.first_name} ${ticket.created_by.last_name}`.trim() || ticket.created_by.email
                          : ''}
                      </Typography>
                      <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                        {formatDistanceToNow(new Date(ticket.created_at), { addSuffix: true })}
                      </Typography>
                    </Stack>
                  </Stack>
                </Box>
              ))
            )}
          </Box>
          <Box sx={{p:1.5,display:'flex',gap:1,alignItems:'center',justifyContent:'space-between'}}><Button size="sm" variant="outlined" disabled={!data?.previous || isFetching} onClick={() => setPage(p => p-1)}>Previous</Button><Typography level="body-xs">Page {page}</Typography><Button size="sm" variant="outlined" disabled={!data?.next || isFetching} onClick={() => setPage(p => p+1)}>Next</Button></Box>
        </Box>

        {/* Right panel — ticket detail or placeholder */}
        <Box sx={{ flex: 1, overflow: 'hidden', display: {xs:selectedTicket?'flex':'none',md:'flex'}, flexDirection: 'column', minWidth: 0 }}>
          {selectedTicket ? (
            <TicketDetail
              ticketId={selectedTicket.id}
              onBack={() => setSelectedTicket(null)}
            />
          ) : (
            <Box
              sx={{
                flex: 1,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
              }}
            >
              <Stack alignItems="center" spacing={1.5} sx={{ color: 'text.tertiary' }}>
                <ConfirmationNumberOutlined size={64} style={{ opacity: 0.3 }} />
                <Typography level="body-lg" sx={{ fontWeight: 500 }}>Select a ticket to view details</Typography>
                <Typography level="body-sm">Choose a ticket from the list to view and respond</Typography>
              </Stack>
            </Box>
          )}
        </Box>
      </Box>
    </Box>
  );
}
